import { Dispatch, SetStateAction } from 'react';
import {
  Box,
  Button,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  type Theme,
} from '@mui/material';
import { Icon24UtilityAdd, Icon24UtilitySubtract, Icon24UtilityChevron } from '@care/react-icons';
import { SxClassProps } from '@/types/common';

const classes: SxClassProps = {
  button: (theme) => ({
    width: 'auto',
    height: 'auto',
    padding: 0,

    '& svg': {
      height: 'auto',

      '& path': {
        fill: `${theme.palette.care.white} !important`,
      },
    },
  }),
  disableButton: (theme) => ({
    '& circle': {
      fill: theme.palette.grey[400],
    },
  }),
};

interface NumberOfChildrenProps {
  numberOfChildren: number;
  setNumberOfChildren: Dispatch<SetStateAction<number>>;
}

const NumberOfChildren = ({ numberOfChildren, setNumberOfChildren }: NumberOfChildrenProps) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isMinimumOrLessValue = numberOfChildren <= 1;
  const isMaximumOrMoreValue = numberOfChildren >= 5;
  const childOrChildren = numberOfChildren < 2 ? 'Child' : 'Children';

  const handleAdd = (numOfChildren: number) => {
    if (numOfChildren < 5) {
      setNumberOfChildren(numOfChildren + 1);
    }
  };

  const handleSubtract = (numOfChildren: number) => {
    if (numOfChildren > 1) {
      setNumberOfChildren(numOfChildren - 1);
    }
  };

  const renderNumberOfChildren = (
    <Box
      display="flex"
      alignItems="center"
      justifyContent={isMobile ? 'space-between' : 'flex-start'}>
      <Box minWidth="22%">
        <Typography variant="h2">
          {numberOfChildren < 5 ? numberOfChildren : `${numberOfChildren}+`} {childOrChildren}
        </Typography>
      </Box>
      <Box display="flex">
        <Button
          onClick={() => handleSubtract(numberOfChildren)}
          disabled={isMinimumOrLessValue}
          data-testid="subtract-button"
          sx={classes.button}>
          <Icon24UtilitySubtract
            size={40}
            sx={isMinimumOrLessValue ? classes.disableButton : undefined}
          />
        </Button>
        <Button
          onClick={() => handleAdd(numberOfChildren)}
          disabled={isMaximumOrMoreValue}
          data-testid="add-button"
          sx={classes.button}>
          <Icon24UtilityAdd
            size={40}
            sx={isMaximumOrMoreValue ? classes.disableButton : undefined}
          />
        </Button>
      </Box>
    </Box>
  );

  const mobileView = (
    <>
      <Typography variant="h3">Number of children</Typography>
      {renderNumberOfChildren}
    </>
  );

  const desktopView = (
    <Accordion elevation={0} defaultExpanded>
      <AccordionSummary
        expandIcon={<Icon24UtilityChevron />}
        sx={{ padding: 0, width: 'fit-content' }}>
        <Typography variant="h3">Number of children</Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={(theme) => ({
          padding: theme.spacing(0, 0, 0, 0.5),
        })}>
        {renderNumberOfChildren}
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box component="section" mb={2}>
      {isMobile ? mobileView : desktopView}
    </Box>
  );
};

export { NumberOfChildren };
